import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { hasCapability, type Capability, type MeResponse } from '@bifeai/shared';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, setApiBranch } from './api';
import { withSnapshot } from './offline';

export const ME_KEY = ['me'] as const;

// Sin internet se usa la última sesión guardada en esta computadora para poder seguir vendiendo.
const fetchMe = () => withSnapshot('me', () => api.get<MeResponse>('/auth/me'));

export function useMeQuery() {
  return useQuery({
    queryKey: ME_KEY,
    queryFn: fetchMe,
    retry: false,
    staleTime: 60_000,
  });
}

/**
 * Después de ingresar, verificar el código o cambiar la contraseña, trae la sesión
 * actualizada ANTES de navegar: así ninguna pantalla decide con datos viejos.
 */
export async function reloadMe(qc: QueryClient): Promise<MeResponse | null> {
  try {
    return await qc.fetchQuery({
      queryKey: ME_KEY,
      queryFn: fetchMe,
      staleTime: 0,
    });
  } catch {
    qc.removeQueries({ queryKey: ME_KEY });
    return null;
  }
}

interface SessionValue {
  me: MeResponse;
  can: (cap: Capability) => boolean;
  /** La casa central (sin sucursal fija) puede elegir sucursal en el selector. */
  isHeadquartersUser: boolean;
  multiBranch: boolean;
  /** La cuenta tiene el módulo Franquicias y la casa central las ve en su sección. */
  franchisesEnabled: boolean;
  /** El usuario es de una franquicia (franquiciado o su equipo). */
  isFranchiseUser: boolean;
  selectedBranchId: string | null;
  /** La casa central eligió una franquicia en el selector: sus chats, pedidos y clientes son privados. */
  privateFranchise: { id: string; name: string } | null;
  setSelectedBranchId: (id: string | null) => void;
  refresh: () => Promise<void>;
}

const SessionContext = createContext<SessionValue | null>(null);
const BRANCH_KEY = 'bf-branch';

function readBranch(): string | null {
  try {
    return window.localStorage.getItem(BRANCH_KEY);
  } catch {
    return null;
  }
}

export function SessionProvider({ me, children }: { me: MeResponse; children: ReactNode }) {
  const qc = useQueryClient();
  const isHeadquartersUser = me.membership.branchId === null;
  const multiBranch = me.tenant.modules.includes('MULTI_BRANCH') && me.branches.length > 1;
  const franchisesEnabled = me.tenant.modules.includes('FRANCHISES');
  const isFranchiseUser = !!me.membership.branchId && !!me.branches.find((b) => b.id === me.membership.branchId)?.franchise;

  const [selected, setSelected] = useState<string | null>(() => {
    if (!isHeadquartersUser) return me.membership.branchId;
    const stored = readBranch();
    return stored && me.branches.some((b) => b.id === stored) ? stored : null;
  });

  // Antes de pintar cualquier consulta, la API ya recibe la sucursal elegida.
  setApiBranch(isHeadquartersUser ? selected : null);
  useEffect(() => setApiBranch(isHeadquartersUser ? selected : null), [selected, isHeadquartersUser]);

  const setSelectedBranchId = useCallback(
    (id: string | null) => {
      setSelected(id);
      try {
        if (id) window.localStorage.setItem(BRANCH_KEY, id);
        else window.localStorage.removeItem(BRANCH_KEY);
      } catch {
        /* sin almacenamiento */
      }
      setApiBranch(id);
      // Todo lo que depende de la sucursal se vuelve a pedir.
      void qc.invalidateQueries({ predicate: (q) => q.queryKey[0] !== 'me' });
    },
    [qc],
  );

  const value = useMemo<SessionValue>(
    () => ({
      me,
      can: (cap) => hasCapability(me.membership.capabilities, cap),
      isHeadquartersUser,
      multiBranch,
      franchisesEnabled,
      isFranchiseUser,
      selectedBranchId: selected,
      privateFranchise: isHeadquartersUser ? (me.branches.find((b) => b.id === selected && b.franchise) ?? null) : null,
      setSelectedBranchId,
      refresh: async () => {
        await reloadMe(qc);
      },
    }),
    [me, isHeadquartersUser, multiBranch, franchisesEnabled, isFranchiseUser, selected, setSelectedBranchId, qc],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession necesita SessionProvider');
  return ctx;
}
